import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../../../contexts/UserContext';
import { useNetwork } from '../../../../utils/network';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CollectionsIcon from '@mui/icons-material/Collections';
import TokenIcon from '@mui/icons-material/Token';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
// import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // KYC hidden from members until launch
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StyleIcon from '@mui/icons-material/Style';
import GavelIcon from '@mui/icons-material/Gavel';

type Props = {
  /* 'rail' is the desktop column, which can collapse to icons and remembers it.
     'drawer' is the mobile overlay: always expanded, no collapse control (there
     is nothing to collapse INTO), and it must close itself when you pick
     something — tapping an item used to navigate and leave the drawer sitting
     open over the page you just asked for. */
  variant?: 'rail' | 'drawer';
  onNavigate?: () => void;
};

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
};

const SIDEBAR_COLLAPSE_KEY = 'incrypt_sidebar_collapsed';

function Sidebar({ variant = 'rail', onNavigate }: Props) {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isDrawer = variant === 'drawer';
  const [collapsedPref, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1'
  );
  const collapsed = isDrawer ? false : collapsedPref;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  /* Which row is the page you are actually on. Paths here are relative to
     /member-portal, and Dashboard's is '' — so compare the last segment, and
     treat the portal root as Dashboard. Case-insensitive because one path is
     'Leaderboard' with a capital L (kept verbatim so routing is unchanged). */
  const current = location.pathname.replace(/\/+$/, '').split('/').pop() || '';
  const isActive = (path: string) =>
    (path === '' ? current === 'member-portal' || current === '' : current.toLowerCase() === path.toLowerCase());

  const isDevOrAdmin = user.role === 'dev' || user.role === 'admin';

  /* INCRYPT BREAK shows only while a break exists (owner 2026-11-04): one public read of the
     current event when the sidebar mounts. Devs and admins always see it, so they can look at an
     empty page. A failed read keeps it hidden. */
  const { get } = useNetwork();
  const getRef = useRef(get);
  getRef.current = get;
  const [hasAuction, setHasAuction] = useState(false);
  useEffect(() => {
    let live = true;
    getRef.current('/card-auctions/current', false)
      .then((r) => { if (live) setHasAuction(!!(r && (r as { auction?: unknown }).auction)); })
      .catch(() => { /* hidden */ });
    return () => { live = false; };
  }, []);

  // Leaderboard sits directly under Play — the standings belong next to the game
  // entry point. Paths are kept verbatim (Dashboard '' and 'Leaderboard' with a
  // capital L) so navigation behaviour is unchanged; only order/presentation.
  const items: NavItem[] = [
    { label: 'Dashboard', path: '', icon: <DashboardIcon /> },
    { label: 'Play', path: 'play', icon: <SportsEsportsIcon /> },
    { label: 'Leaderboard', path: 'Leaderboard', icon: <LeaderboardIcon /> },
    { label: 'Subscription', path: 'subscription', icon: <SubscriptionsIcon /> },
    { label: 'Giveaway', path: 'giveaway', icon: <CardGiftcardIcon /> },
    { label: 'Digital Collectibles', path: 'digital-collectibles', icon: <CollectionsIcon /> },
    { label: 'Physical Cards', path: 'physical-cards', icon: <StyleIcon /> },
    ...(hasAuction || isDevOrAdmin
      ? [{ label: 'Incrypt Break', path: 'incrypt-break', icon: <GavelIcon /> }]
      : []),
    { label: 'Token', path: 'token', icon: <TokenIcon /> },
    { label: 'Marketplace', path: 'marketplace', icon: <StorefrontIcon /> },
    {
      label: 'Market Watch',
      path: 'market-watch',
      icon: <ShowChartIcon />,
      disabled: true
    },
    { label: 'White Paper', path: 'white-paper', icon: <DescriptionIcon /> },
    { label: 'Roadmap', path: 'roadmap', icon: <MapIcon /> },
    { label: 'Contact Us', path: 'contact', icon: <MailOutlineIcon /> },
    { label: 'SN', path: 'sn', icon: <HubIcon />, disabled: true },
    { label: 'Settings', path: 'settings', icon: <SettingsIcon /> },
    /* No sidebar link for KYC on purpose. Members reach it through the free-pack tile on the
       dashboard, which is the launch surface we chose so the funnel can be watched before
       everyone is pushed at it. The page itself lives at /member-portal/kyc. */
    ...(isDevOrAdmin
      ? [{ label: 'Play (Dev)', path: 'play-dev', icon: <SportsEsportsIcon /> }]
      : [])
  ];

  return (
    <Box
      className="sidebar"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: isDrawer ? '100%' : collapsed ? '68px' : '228px',
        flexShrink: 0,
        transition: 'width 0.2s ease',
        paddingX: isDrawer ? '12px' : collapsed ? '6px' : '1%'
      }}
    >
      {!isDrawer && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
            paddingTop: '4px'
          }}
        >
          <IconButton
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          overflowY: 'auto',
          overflowX: 'hidden',
          /* room for the phone's home indicator, and enough that the last row
             (Marketplace) is never half-cut by the bottom of the sheet */
          paddingBottom: isDrawer ? 'calc(24px + env(safe-area-inset-bottom))' : '10%'
        }}
      >
        {items.map((item) => (
          <Tooltip
            key={item.label}
            title={item.label}
            placement="right"
            arrow
            disableHoverListener={!collapsed}
          >
            <Box component="span" sx={{ width: '100%', display: 'flex' }}>
              <Button
                className="sidebar-button"
                onClick={() => handleNavigation(item.path)}
                disabled={item.disabled}
                startIcon={collapsed ? undefined : item.icon}
                /* Flat 50%-white pills read as "disabled button" against a dark
                   background, and every row looked identical whether or not you
                   were on it. Translucent ink + a left accent bar on the active
                   row instead: the page you are on is now obvious, and the list
                   stops competing with the content behind it. */
                sx={{
                  position: 'relative',
                  width: '100%',
                  minWidth: 0,
                  padding: collapsed ? '10px 0' : '9px 14px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  textAlign: 'left',
                  backgroundColor: isActive(item.path)
                    ? 'rgba(56,232,255, 0.16)'
                    : 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid',
                  borderColor: isActive(item.path)
                    ? 'rgba(56,232,255, 0.55)'
                    : 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  color: isActive(item.path) ? '#8af5ff' : 'rgba(255,255,255,0.88)',
                  fontWeight: isActive(item.path) ? 700 : 500,
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  transition: 'background-color .18s ease, border-color .18s ease',
                  '&:hover': {
                    backgroundColor: isActive(item.path)
                      ? 'rgba(56,232,255, 0.22)'
                      : 'rgba(255, 255, 255, 0.12)'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255,255,255,0.3)',
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(255,255,255,0.05)'
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: '10px',
                    color: isActive(item.path) ? '#38e8ff' : 'rgba(255,255,255,0.55)'
                  },
                  '& .MuiButton-startIcon > *': { fontSize: '19px' }
                }}
              >
                {collapsed ? item.icon : item.label}
              </Button>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}

export default Sidebar;
